import "@/App.css";
import { useCallback, useEffect, useState } from "react";
import { Activity, BarChart3, BookOpen, Sliders, ShieldCheck, Zap } from "lucide-react";
import { Toaster } from "sonner";
import { useTheme } from "@/lib/theme";
import { api } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { PositionAlarm } from "@/components/PositionAlarm";
import { LoginPage, UserBadge } from "@/components/LoginPage";
import LiveTab from "@/pages/LiveTab";
import BacktestTab from "@/pages/BacktestTab";
import LedgerTab from "@/pages/LedgerTab";
import SettingsTab from "@/pages/SettingsTab";
import AccountTab from "@/pages/AccountTab";
import ExecutionTab from "@/pages/ExecutionTab";

const TABS = [
  { id: "live", label: "Live", icon: Activity, el: LiveTab },
  { id: "backtest", label: "Backtest", icon: BarChart3, el: BacktestTab },
  { id: "paper-ledger", label: "Paper Ledger", icon: BookOpen, el: LedgerTab },
  { id: "execution", label: "Live Trading", icon: Zap, el: ExecutionTab },
  { id: "settings", label: "Settings", icon: Sliders, el: SettingsTab },
  { id: "account", label: "Account", icon: ShieldCheck, el: AccountTab },
];

function Dashboard({ user, onLogout }) {
  const theme = useTheme();
  const [tab, setTab] = useState("live");
  const [status, setStatus] = useState(null);
  const loadStatus = useCallback(() => api.get("/status").then((r) => setStatus(r.data)).catch(() => {}), []);

  useEffect(() => {
    loadStatus();
    const id = setInterval(loadStatus, 30000);
    return () => clearInterval(id);
  }, [loadStatus]);

  const Page = TABS.find((t) => t.id === tab).el;
  return (
    <div className="min-h-screen bg-[#080C14] text-slate-100">
      <TopBar status={status} onGoSettings={() => setTab("settings")} onGoLive={() => setTab("execution")} onGoBacktest={() => setTab("backtest")} />
      <PositionAlarm alarm={status?.position_alarm} onRecheck={loadStatus} />
      <nav className="flex gap-1 overflow-x-auto border-b border-slate-800 bg-[#0D121F] px-3 sm:px-4" data-testid="nav-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`tab-button-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-xs font-medium uppercase tracking-wider transition-colors duration-150 ${
              tab === t.id ? "border-indigo-400 text-slate-50" : "border-transparent text-slate-500 hover:text-slate-300"
            }`}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
        <UserBadge user={user} onLogout={onLogout} />
      </nav>
      <main className="p-3 sm:p-4 lg:p-5">
        <Page status={status} reloadStatus={loadStatus} />
      </main>
      <Toaster theme={theme} position="bottom-right" richColors />
    </div>
  );
}

export default function App() {
  const theme = useTheme();
  const [user, setUser] = useState(null);
  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
    const out = () => setUser(false);
    window.addEventListener("auth:logout", out);
    return () => window.removeEventListener("auth:logout", out);
  }, []);
  const logout = () => api.post("/auth/logout").finally(() => setUser(false));
  if (user === null) return <div className="min-h-screen bg-[#080C14]" data-testid="auth-checking" />;
  if (!user) return (<><LoginPage onDone={setUser} /><Toaster theme={theme} position="bottom-right" richColors /></>);
  return <Dashboard user={user} onLogout={logout} />;
}
